import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LANGUAGES } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Globe, Check, ArrowRight } from 'lucide-react';

const LanguageSelector = ({ selectedLanguage, onSelectLanguage, onContinue }) => {
    const continueButtonRef = useRef(null);
    
    // Auto-scroll to continue button when language is selected
    useEffect(() => {
        if (selectedLanguage && continueButtonRef.current) {
            // Small delay to allow the UI to update
            setTimeout(() => {
                continueButtonRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 300);
        }
    }, [selectedLanguage]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full">
                {/* Logo and Header */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                        <Globe className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                        Welcome to MediGuide
                    </h1>
                    <p className="text-muted-foreground">
                        Select your preferred language
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Choisissez votre langue • Wählen Sie Ihre Sprache • Выберите язык
                    </p>
                </div>

                {/* Language Grid */}
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-6 mb-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {LANGUAGES.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => onSelectLanguage(lang.code)}
                                className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:scale-105 hover:shadow-md ${
                                    selectedLanguage === lang.code
                                        ? 'border-primary bg-primary/10 shadow-md'
                                        : 'border-slate-200 dark:border-slate-700 hover:border-primary/50'
                                }`}
                                data-testid={`welcome-lang-${lang.code}`}
                            >
                                {selectedLanguage === lang.code && (
                                    <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                        <Check className="w-3 h-3 text-white" />
                                    </div>
                                )}
                                <span className="text-3xl">{lang.flag}</span>
                                <div className="text-center">
                                    <p className="font-semibold text-sm">{lang.name}</p>
                                    <p className="text-xs text-muted-foreground">{lang.nativeName}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Continue Button */}
                <div ref={continueButtonRef}>
                    <Button
                        onClick={onContinue}
                        size="lg"
                        className={`w-full rounded-xl h-14 text-lg font-semibold shadow-lg transition-all duration-300 ${
                            selectedLanguage 
                                ? 'bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 animate-pulse' 
                                : 'bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90'
                        }`}
                        data-testid="welcome-continue-btn"
                    >
                        Continue
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    {/* Visual hint when language is selected */}
                    {selectedLanguage && (
                        <p className="text-center text-sm text-primary font-medium mt-2 animate-bounce">
                            ↑ Tap to continue
                        </p>
                    )}
                </div>

                {/* Footer note */}
                <p className="text-center text-xs text-muted-foreground mt-4">
                    You can change your language anytime in Settings
                </p>

                {/* Legal Links - Required for Google OAuth verification */}
                <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <Link 
                        to="/privacy" 
                        className="text-sm text-primary hover:underline font-medium"
                        data-testid="welcome-privacy-link"
                    >
                        Privacy Policy
                    </Link>
                    <span className="text-slate-300 dark:text-slate-600">•</span>
                    <Link 
                        to="/terms" 
                        className="text-sm text-primary hover:underline font-medium"
                        data-testid="welcome-terms-link"
                    >
                        Terms of Service
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LanguageSelector;
